import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ChevronDown, Zap, Shield, Users, Target, Lightbulb, Layers, Code2, Rocket, Search } from "lucide-react";
import Breadcrumb from "../components/Breadcrumb";
import SectionLabel from "../components/SectionLabel";
import ScrollReveal from "../components/ScrollReveal";
import { FULL_PROCESS } from "../lib/data";

/* ── Animated counter hook ── */
function useCountUp(target, duration = 1400, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

/* ── Stats strip ── */
const STATS = [
  { num: 40, suffix: "+", label: "Projects Delivered" },
  { num: 5, suffix: " phases", label: "Per every project" },
  { num: 30, suffix: "-day", label: "Post-launch support" },
  { num: 100, suffix: "%", label: "Client satisfaction" },
];

function StatCard({ stat }) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  const count = useCountUp(stat.num, 1200, active);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setActive(true); obs.unobserve(node); } },
      { threshold: 0.4 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="proc-stat-card">
      <div className="proc-stat-num">
        {count}
        <span className="proc-stat-suffix">{stat.suffix}</span>
      </div>
      <div className="proc-stat-label">{stat.label}</div>
    </div>
  );
}

/* ── Phase icon map ── */
const PHASE_ICONS = [Search, Lightbulb, Layers, Code2, Rocket];

/* ── Per-phase detail data ── */
const PHASE_EXTRAS = [
  {
    deliverables: ["Creative brief document", "Audience persona map", "Competitor teardown", "Success metrics definition"],
    tools: ["Notion", "Google Docs", "Miro", "Desk research"],
    color: "#6366f1",
  },
  {
    deliverables: ["Mood board (3 directions)", "Reference collection", "AI-assisted concept sketches", "Chosen creative direction"],
    tools: ["Midjourney", "Figma", "Pinterest", "Adobe Firefly"],
    color: "#f59e0b",
  },
  {
    deliverables: ["All screens / brand assets", "Component library", "Clickable prototype (UI)", "Complete identity system"],
    tools: ["Figma", "Illustrator", "Photoshop", "After Effects"],
    color: "#10b981",
  },
  {
    deliverables: ["Exported files (all formats)", "Developer spec & tokens", "Walkthrough video", "Source files + guidelines"],
    tools: ["Figma", "Zeroheight", "Loom", "WeTransfer"],
    color: "#3b82f6",
  },
  {
    deliverables: ["Final QA checklist", "Launch-day monitoring", "Post-launch tweaks", "Post-launch support window"],
    tools: ["Chrome DevTools", "Hotjar", "Linear", "Slack"],
    color: "#ec4899",
  },
];

/* ── Principles ── */
const PRINCIPLES = [
  { icon: Zap, title: "Speed without shortcuts", body: "AI tools compress timelines dramatically. You get more iterations, faster — not less care." },
  { icon: Shield, title: "Transparent communication", body: "Every decision is explained. No mystery, no jargon. You always know exactly where we are." },
  { icon: Users, title: "One person, full ownership", body: "You work directly with me — not an account manager. That's faster decisions and tighter output." },
  { icon: Target, title: "Results over aesthetics", body: "Pretty is a by-product. The real goal is work that performs: converts, retains and grows." },
];

/* ── FAQ ── */
const FAQ = [
  { q: "How long does a typical project take?", a: "Every project is scoped individually — timeline depends on complexity, scope and how quickly feedback rounds move. I'll give you a clear, realistic schedule during the brief phase before any work begins." },
  { q: "How do revisions work?", a: "Every package includes structured revision rounds (usually 2–3). We review at the end of each phase — not in a continuous back-and-forth — so feedback is focused and efficient." },
  { q: "What happens if I need changes after delivery?", a: "Every project includes a post-launch support window for minor tweaks at no extra charge. Larger scope changes are quoted separately." },
  { q: "Do you take on multiple projects at once?", a: "I limit myself to 2–3 active projects at a time so your work always gets real focus. Slots fill fast — it's worth booking early." },
  { q: "Can I join mid-process if I already have some brand work done?", a: "Absolutely. We start wherever you are. I'll run a quick audit to understand what's worth keeping and build from there." },
  { q: "Do you work with international clients?", a: "Yes — fully remote and async-friendly. I've worked with clients in the UK, UAE and across India. Time zones have never been an issue." },
];

function FAQItem({ item, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`proc-faq-item ${open ? "open" : ""}`}>
      <button className="proc-faq-q" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>{item.q}</span>
        <ChevronDown className="proc-faq-chevron" size={18} />
      </button>
      <div className="proc-faq-body">
        <p>{item.a}</p>
      </div>
    </div>
  );
}

/* ── Main page ── */
export default function Process() {
  return (
    <div className="page" data-testid="process-page">

      {/* ─── Hero ─── */}
      <section className="page-hero">
        <div className="container-x">
          <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Process", to: "/process" }]} />
          <h1 style={{ marginTop: 16 }}>How I Work.</h1>
          <p className="lead">
            Five sharp phases. No ceremony. Built to maintain momentum and ship something we're both proud of.
          </p>
        </div>
      </section>

      {/* ─── Stats strip ─── */}
      <section className="section-bright" style={{ padding: "48px 0", borderBottom: "1px solid var(--alabaster-grey)" }}>
        <div className="container-x">
          <div className="proc-stats-grid">
            {STATS.map((s, i) => <StatCard key={i} stat={s} />)}
          </div>
        </div>
      </section>

      {/* ─── Phases ─── */}
      <section className="section section-bright">
        <div className="container-x">
          <SectionLabel num={1}>The Phases</SectionLabel>
          <h2 className="sec-title">From brief to launch — and beyond.</h2>
          <p className="sec-sub">Each phase has clear inputs, outputs and a sign-off before we move forward. No surprises.</p>

          <div className="proc-list" style={{ marginTop: 56 }}>
            {FULL_PROCESS.map((s, i) => {
              const Icon = PHASE_ICONS[i];
              const extra = PHASE_EXTRAS[i];
              return (
                <ScrollReveal key={s.num} delay={i * 80}>
                  <div className="proc-detail-rich" data-testid={`proc-detail-${s.num}`}>
                    {/* Left col */}
                    <div className="proc-detail-left">
                      <div className="proc-icon-wrap" style={{ background: extra.color + "18", border: `1px solid ${extra.color}30` }}>
                        <Icon size={22} style={{ color: extra.color }} />
                      </div>
                      <div className="num-big" style={{ color: extra.color + "55" }}>{s.num}</div>
                      <div className="proc-phase-tag">Phase {s.num}</div>
                    </div>

                    {/* Right col */}
                    <div className="proc-detail-right">
                      <h3>{s.t}</h3>
                      <p>{s.d}</p>

                      <div className="proc-extras-row">
                        {/* Deliverables */}
                        <div className="proc-extras-block">
                          <div className="proc-extras-heading">Deliverables</div>
                          <ul className="proc-deliverables">
                            {extra.deliverables.map((d, j) => (
                              <li key={j}>
                                <CheckCircle2 size={14} style={{ color: extra.color, flexShrink: 0 }} />
                                {d}
                              </li>
                            ))}
                          </ul>
                        </div>
                        {/* Tools */}
                        <div className="proc-extras-block">
                          <div className="proc-extras-heading">Tools</div>
                          <div className="proc-tools-list">
                            {extra.tools.map((t, j) => (
                              <span key={j} className="proc-tool-pill" style={{ borderColor: extra.color + "40", color: extra.color }}>
                                {t}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Accent line */}
                    <div className="proc-accent-bar" style={{ background: extra.color }} />
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Principles ─── */}
      <section className="section section-platinum">
        <div className="container-x">
          <SectionLabel num={2}>Principles</SectionLabel>
          <h2 className="sec-title">The values behind the work.</h2>
          <div className="proc-principles-grid">
            {PRINCIPLES.map((p, i) => {
              const Icon = p.icon;
              return (
                <ScrollReveal key={i} delay={i * 70}>
                  <div className="proc-principle-card">
                    <div className="proc-principle-icon">
                      <Icon size={20} />
                    </div>
                    <h4>{p.title}</h4>
                    <p>{p.body}</p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="section section-platinum">
        <div className="container-x">
          <SectionLabel num={3}>FAQ</SectionLabel>
          <h2 className="sec-title">Common questions.</h2>
          <div className="proc-faq-list">
            {FAQ.map((item, i) => (
              <ScrollReveal key={i} delay={i * 50}>
                <FAQItem item={item} idx={i} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <div className="container-x" style={{ paddingBottom: 96 }}>
        <div className="cta-dark">
          <div className="lead">Ready to start?</div>
          <h2>Let's run this playbook on your project.</h2>
          <Link to="/contact" className="btn btn-light">Get In Touch <ArrowRight size={16} /></Link>
        </div>
      </div>
    </div>
  );
}
